import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { errMsg } from "@/lib/errors";
import { Copy, Users, Gift, Share2 } from "lucide-react";
import ShareButtons from "@/components/ShareButtons";

export default function ReferralsPanel() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/artist/referrals").then((r) => setData(r.data)).catch(() => setData({})); }, []);

  if (!data) return <div className="p-6 font-mono text-xs text-gray-500 uppercase">Loading...</div>;
  const link = `${window.location.origin}/register?ref=${data.code}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); toast.success("Referral link copied"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="max-w-3xl">
      <div className="border border-[#2A2A2A] bg-[#121212] p-6 md:p-8 mb-6">
        <div className="flex items-center gap-2 mb-4"><Gift className="h-5 w-5 text-[#FF4500]" /><h3 className="font-display text-2xl">Referral rewards</h3></div>
        <p className="font-mono text-xs text-gray-400 leading-relaxed mb-6">
          Invite another artist. When they subscribe to the $2 plan, <span className="text-[#FF4500] font-bold">you both get 1 free month</span>. No cap.
        </p>

        <div className="grid grid-cols-3 gap-3 mb-6">
          <Stat label="Invited" value={data.referrals_count || 0} />
          <Stat label="Converted" value={data.converted_count || 0} />
          <Stat label="Free months earned" value={data.free_months_earned || 0} accent />
        </div>

        <div className="border border-[#2A2A2A] bg-black p-3 flex items-center gap-2 font-mono text-xs text-gray-300 mb-3 overflow-hidden">
          <span className="text-[#FF4500] shrink-0">→</span>
          <span data-testid="ref-link" className="truncate flex-1">{link}</span>
          <Button data-testid="ref-copy" size="sm" onClick={copy}
                  className="rounded-none bg-[#FF4500] hover:bg-[#E53E00] text-black font-mono text-[10px] uppercase h-8 px-3 shrink-0">
            <Copy className="h-3 w-3 mr-1" /> Copy
          </Button>
        </div>

        <ShareButtons url={link} title={`Join me on MeloCraft — the music marketplace where artists keep 100%. Use my link for a free month:`} />
      </div>

      {data.invited?.length > 0 && (
        <div className="border border-[#2A2A2A] bg-[#121212]">
          <div className="flex items-center gap-2 px-6 py-4 border-b border-[#2A2A2A]">
            <Users className="h-4 w-4 text-[#FF4500]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-gray-400">Your invitees</span>
          </div>
          <div className="divide-y divide-[#2A2A2A]">
            {data.invited.map((u) => (
              <div key={u.username} className="px-6 py-3 grid grid-cols-[1fr_auto] items-center gap-3">
                <div>
                  <div className="font-display">{u.display_name || u.username}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">@{u.username}</div>
                </div>
                <div className={`font-mono text-[10px] uppercase tracking-widest ${u.subscription_active ? "text-[#FF4500]" : "text-gray-500"}`}>
                  {u.subscription_active ? "· CONVERTED" : "· PENDING"}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent = false }) {
  return (
    <div className={`border p-4 ${accent ? "border-[#FF4500]/50 bg-[#FF4500]/5" : "border-[#2A2A2A] bg-black/30"}`}>
      <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">{label}</div>
      <div className={`font-display text-2xl md:text-3xl mt-1 ${accent ? "text-[#FF4500]" : ""}`}>{value}</div>
    </div>
  );
}
